import type React from 'react';
import { logger } from '../utils/core/logger';
import SvgMaskIcon from './SvgMaskIcon';

interface EvaluationButtonsProps {
    onEvaluate: (rating: 'thumbs_up' | 'thumbs_down') => void;
    isEvaluated: boolean;
    evaluationRating?:
        | 'Accurate & OK'
        | 'Accurate but Not OK'
        | 'Not Accurate but OK'
        | 'Not Accurate & Not OK'
        | null;
}

// The visible tint comes from the CSS mask's background-color (see SvgMaskIcon). The path's
// own fill/stroke only carry the mask's alpha: opaque stroke → full color, translucent fill
// → 15% color when active. This matches the prior stroke + fillOpacity 0.15 look.
const thumbInner = (path: string, isActive: boolean) =>
    `<path d="${path}" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="${
        isActive ? '#000' : 'none'
    }" fill-opacity="${isActive ? '0.15' : '0'}"/>`;

const THUMBS_UP_PATH =
    'M7 22H4C3.46957 22 2.96086 21.7893 2.58579 21.4142C2.21071 21.0391 2 20.5304 2 20V13C2 12.4696 2.21071 11.9609 2.58579 11.5858C2.96086 11.2107 3.46957 11 4 11H7M14 9V5C14 4.20435 13.6839 3.44129 13.1213 2.87868C12.5587 2.31607 11.7956 2 11 2L7 11V22H18.28C18.7623 22.0055 19.2304 21.8364 19.5979 21.524C19.9654 21.2116 20.2077 20.7769 20.28 20.3L21.66 11.3C21.7035 11.0134 21.6842 10.7207 21.6033 10.4423C21.5225 10.1638 21.3821 9.90629 21.1919 9.68751C21.0016 9.46873 20.7661 9.29393 20.5016 9.17522C20.2371 9.0565 19.9499 8.99672 19.66 9H14Z';

const THUMBS_DOWN_PATH =
    'M17 2H20C20.5304 2 21.0391 2.21071 21.4142 2.58579C21.7893 2.96086 22 3.46957 22 4V11C22 11.5304 21.7893 12.0391 21.4142 12.4142C21.0391 12.7893 20.5304 13 20 13H17M10 15V19C10 19.7956 10.3161 20.5587 10.8787 21.1213C11.4413 21.6839 12.2044 22 13 22L17 13V2H5.72C5.23767 1.99448 4.76962 2.16359 4.40206 2.47599C4.03451 2.78839 3.79232 3.22305 3.72 3.7L2.34 12.7C2.29649 12.9866 2.31583 13.2793 2.39668 13.5577C2.47753 13.8362 2.61791 14.0937 2.80816 14.3125C2.99841 14.5313 3.23394 14.7061 3.49843 14.8248C3.76291 14.9435 4.05009 15.0033 4.34 15H10Z';

const ThumbIcon: React.FC<{ path: string; color: string; size?: number; isActive?: boolean }> = ({
    path,
    color,
    size = 18,
    isActive = false,
}) => (
    <SvgMaskIcon
        viewBox="0 0 24 24"
        inner={thumbInner(path, isActive)}
        width={size}
        height={size}
        color={color}
        style={{
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isActive ? 'scale(1.1)' : 'scale(1)',
        }}
    />
);

const ThumbsUpIcon: React.FC<{ color: string; size?: number; isActive?: boolean }> = (props) => (
    <ThumbIcon path={THUMBS_UP_PATH} {...props} />
);

const ThumbsDownIcon: React.FC<{ color: string; size?: number; isActive?: boolean }> = (props) => (
    <ThumbIcon path={THUMBS_DOWN_PATH} {...props} />
);

const EvaluationButtons: React.FC<EvaluationButtonsProps> = ({
    onEvaluate,
    isEvaluated,
    evaluationRating,
}) => {
    const getButtonColor = (buttonType: 'thumbs_up' | 'thumbs_down') => {
        if (isEvaluated && evaluationRating) {
            if (buttonType === 'thumbs_up' && evaluationRating === 'Accurate & OK') {
                return 'var(--rw-bg-action-primary)';
            }
            if (
                buttonType === 'thumbs_down' &&
                (evaluationRating === 'Accurate but Not OK' ||
                    evaluationRating === 'Not Accurate & Not OK' ||
                    evaluationRating === 'Not Accurate but OK')
            ) {
                return '#ef4444';
            }
        }
        return 'var(--rw-icon-muted)';
    };

    const getButtonOpacity = (buttonType: 'thumbs_up' | 'thumbs_down') => {
        if (isEvaluated && evaluationRating) {
            if (buttonType === 'thumbs_up' && evaluationRating !== 'Accurate & OK') {
                return 0.4;
            }
            if (buttonType === 'thumbs_down' && evaluationRating === 'Accurate & OK') {
                return 0.4;
            }
        }
        return 1;
    };

    const isThumbsUpActive = isEvaluated && evaluationRating === 'Accurate & OK';
    const isThumbsDownActive =
        isEvaluated && evaluationRating && evaluationRating !== 'Accurate & OK';

    const getBackgroundColor = (buttonType: 'thumbs_up' | 'thumbs_down') => {
        if (isEvaluated && evaluationRating) {
            if (buttonType === 'thumbs_up' && evaluationRating === 'Accurate & OK') {
                return 'var(--rw-bg-selection-accent)';
            }
            if (buttonType === 'thumbs_down' && evaluationRating !== 'Accurate & OK') {
                return '#ef444415';
            }
        }
        return 'transparent';
    };

    const handleThumbsUp = () => {
        if (!isEvaluated) {
            onEvaluate('thumbs_up');
        }
    };

    const handleThumbsDown = () => {
        logger.debug('EvaluationButtons: thumbs down clicked, isEvaluated=', isEvaluated);
        if (!isEvaluated) {
            logger.debug('EvaluationButtons: calling onEvaluate with thumbs_down');
            onEvaluate('thumbs_down');
        } else {
            logger.debug('EvaluationButtons: message already evaluated, ignoring click');
        }
    };

    return (
        <div
            className="chat-evaluation-buttons-container"
            data-ph-capture-attribute-section="feedback-controls"
            style={{
                display: 'flex',
                gap: '6px',
                alignItems: 'center',
                padding: '8px 0',
            }}
        >
            <button
                type="button"
                className="chat-feedback-thumbs-up-button"
                data-ph-capture-attribute-feedback-type="positive"
                data-ph-capture-attribute-evaluated={isEvaluated}
                data-ph-capture-attribute-rating={evaluationRating}
                onClick={handleThumbsUp}
                disabled={isEvaluated}
                style={{
                    background: getBackgroundColor('thumbs_up'),
                    border: 'none',
                    cursor: isEvaluated ? 'default' : 'pointer',
                    padding: '8px 12px',
                    borderRadius: 'var(--rw-radius-card)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    fontWeight: 'var(--rw-font-weight-medium)',
                    color: getButtonColor('thumbs_up'),
                    opacity: getButtonOpacity('thumbs_up'),
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '36px',
                    boxShadow: isThumbsUpActive
                        ? '0 2px 8px rgb(var(--rw-accent-rgb) / 0.2)'
                        : '0 1px 3px rgba(0, 0, 0, 0.05)',
                }}
                onMouseEnter={(e) => {
                    if (!isEvaluated) {
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isEvaluated) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                    }
                }}
                onMouseDown={(e) => {
                    if (!isEvaluated) {
                        e.currentTarget.style.transform = 'translateY(0) scale(0.98)';
                    }
                }}
                onMouseUp={(e) => {
                    if (!isEvaluated) {
                        e.currentTarget.style.transform = 'translateY(-1px) scale(1)';
                    }
                }}
                title={isEvaluated ? 'Already evaluated' : 'Helpful response'}
            >
                <ThumbsUpIcon color={getButtonColor('thumbs_up')} isActive={isThumbsUpActive} />
                {isEvaluated && evaluationRating === 'Accurate & OK' && (
                    <span
                        className="chat-feedback-status-text"
                        style={{ fontSize: '13px', fontWeight: 500 }}
                    >
                        Accurate & OK
                    </span>
                )}
            </button>

            <button
                type="button"
                className="chat-feedback-thumbs-down-button"
                data-ph-capture-attribute-feedback-type="negative"
                data-ph-capture-attribute-evaluated={isEvaluated}
                data-ph-capture-attribute-rating={evaluationRating}
                onClick={handleThumbsDown}
                disabled={isEvaluated}
                style={{
                    background: getBackgroundColor('thumbs_down'),
                    border: 'none',
                    cursor: isEvaluated ? 'default' : 'pointer',
                    padding: '8px 12px',
                    borderRadius: 'var(--rw-radius-card)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    fontWeight: 'var(--rw-font-weight-medium)',
                    color: getButtonColor('thumbs_down'),
                    opacity: getButtonOpacity('thumbs_down'),
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '36px',
                    boxShadow: isThumbsDownActive
                        ? '0 2px 8px #ef444420'
                        : '0 1px 3px rgba(0, 0, 0, 0.05)',
                }}
                onMouseEnter={(e) => {
                    if (!isEvaluated) {
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isEvaluated) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                    }
                }}
                onMouseDown={(e) => {
                    if (!isEvaluated) {
                        e.currentTarget.style.transform = 'translateY(0) scale(0.98)';
                    }
                }}
                onMouseUp={(e) => {
                    if (!isEvaluated) {
                        e.currentTarget.style.transform = 'translateY(-1px) scale(1)';
                    }
                }}
                title={isEvaluated ? 'Already evaluated' : 'Needs improvement'}
            >
                <ThumbsDownIcon
                    color={getButtonColor('thumbs_down')}
                    isActive={!!isThumbsDownActive}
                />
                {isEvaluated && evaluationRating && evaluationRating !== 'Accurate & OK' && (
                    <span
                        className="chat-feedback-status-text"
                        style={{ fontSize: '13px', fontWeight: 500 }}
                    >
                        Evaluated
                    </span>
                )}
            </button>
        </div>
    );
};

export default EvaluationButtons;
